import { useSyncExternalStore } from 'react';
import { tokenStore } from '@/lib/token-store';
import { isNative } from '@/platform/native';

/*
 * The one loading screen of signing in, signing up and returning from Google (see AuthGate.tsx). This is its state, outside React so the auth code, the
 * route guards and the Suspense fallbacks can all talk to it without a provider:
 *   idle       no gate: the app is shown as usual
 *   working    a sign-in/sign-up/Google exchange is under way (the destination is not known yet): the gate stays
 *   navigated  the destination was chosen and the router is on it (or this is a cold load with a stored session): the gate stays until the
 *              destination is ready, see AuthGate
 * `holds` counts things that are still loading behind the gate (a lazy route's Suspense fallback, the first-run check). Nothing here decides
 * where anyone goes: it only decides what is SHOWN meanwhile.
 */
export type GatePhase = 'idle' | 'working' | 'navigated';
interface GateState {
  phase: GatePhase;
  holds: number;
}

/** Pages on which a cold load with a stored session shows the gate (everything the signed-in app, sign-in and sign-up can land on). Marketing pages never wait. */
const GATED_COLD_LOAD = /^\/(admin|staff|crew|dashboard|login|register|oauth\/consent|change-email|change-password|profile|billing)(\/|$)/;

function initialPhase(): GatePhase {
  if (typeof window === 'undefined') return 'idle';
  const path = window.location.pathname;
  // coming back from Google: the exchange has not started yet, so the destination is unknown
  if (path === '/auth/google/callback') return 'working';
  return hasStoredSession() && GATED_COLD_LOAD.test(path) ? 'navigated' : 'idle';
}

/** A stored session (the access token cookie): the same signal the auth provider uses to decide whether it has anything to restore. */
export function hasStoredSession(): boolean {
  try {
    // the web keeps the access token in a cookie; the app keeps only a refresh token (its access token is in memory, after the first refresh)
    return Boolean(isNative() ? tokenStore.getRefresh() : tokenStore.getAccess());
  } catch {
    return false;
  }
}

let state: GateState = { phase: 'idle', holds: 0 };
let initialised = false;
const listeners = new Set<() => void>();
const ensure = () => {
  if (!initialised) {
    initialised = true;
    state = { ...state, phase: initialPhase() };
  }
};
const set = (next: Partial<GateState>) => {
  ensure();
  state = { ...state, ...next };
  listeners.forEach((l) => l());
};

export const beginSettling = () => set({ phase: 'working' });
/** The destination is chosen and the router moved: from here the gate waits for the destination to be ready. */
export const markNavigated = () => set({ phase: 'navigated' });
export const endSettling = () => {
  if (state.phase !== 'idle') set({ phase: 'idle' });
};
/** Tests: back to a fresh page load. */
export const resetGate = (phase?: GatePhase) => {
  initialised = phase === undefined ? false : true;
  state = { phase: phase ?? 'idle', holds: 0 };
  listeners.forEach((l) => l());
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const snapshot = () => {
  ensure();
  return state;
};

export const useGateState = (): GateState => useSyncExternalStore(subscribe, snapshot, snapshot);

/** While `active`, the gate does not open: something behind it is still loading. */
export function addHold(): () => void {
  set({ holds: state.holds + 1 });
  let released = false;
  return () => {
    if (released) return;
    released = true;
    set({ holds: Math.max(0, state.holds - 1) });
  };
}
